import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api, authHeaders, describeError } from "../lib/api";
import type { ConsultationStatus } from "../lib/api";
import { num, shortId } from "../lib/format";
import { formatAgoLo } from "../lib/requestStatus";
import {
  CONSULT_STATUS_LABEL,
  CONSULT_STATUS_ORDER,
  CONSULT_TOPIC_LABEL,
  CONTACT_MODE_LABEL,
  canReviewConsultations,
  consultStatusBadge,
  dateTimeLo,
  placeLabel,
} from "../lib/services";
import { ConsultationsStatsPanel } from "../components/servicePanels";
import { Lock } from "../components/icons";

const PAGE_LIMIT = 200;

async function fetchQueue(status: ConsultationStatus | null) {
  const { data, error, response } = await api.GET("/v1/admin/consultations", {
    params: { query: { limit: PAGE_LIMIT, ...(status ? { status: [status] } : {}) } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data;
}

/**
 * ຄິວນັດປຶກສາ (ADR-0027) — เฉพาะ officer/super admin (`CanReviewRequests()`) · รายการไม่มีเนื้อหา (`redacted`)
 * ต้องเปิดทีละเรื่อง ซึ่ง server บันทึก audit `consultation.viewed` ให้
 *
 * การเงินเมือง/กวดกาแขวงไม่มีสิทธิ์อ่าน (API ตอบ 403) — เข้ามาทาง URL ตรงจะเห็นแค่สถิติจำนวนแทน
 */
export function ConsultationsPage() {
  const reviewer = canReviewConsultations();
  return reviewer ? <ConsultationQueue /> : <ConsultationStatsOnly />;
}

function ConsultationStatsOnly() {
  return (
    <div>
      <header className="page-header">
        <div>
          <h1 className="page-header__title">ນັດປຶກສາ</h1>
          <p className="page-header__subtitle">ສະຖິຕິການນັດປຶກສາໃນເຂດ</p>
        </div>
      </header>
      <p className="lock-note" style={{ marginBottom: "var(--space-4)" }}>
        <Lock size={14} /> ເນື້ອໃນການປຶກສາເປັນຄວາມລັບ — ສະເພາະຕາແສງທີ່ຮັບຜິດຊອບອ່ານໄດ້
      </p>
      <ConsultationsStatsPanel />
    </div>
  );
}

function ConsultationQueue() {
  const [status, setStatus] = useState<ConsultationStatus | null>("requested");
  const list = useQuery({ queryKey: ["consultations", "admin", status], queryFn: () => fetchQueue(status) });
  const counts = list.data?.counts;
  const total = counts ? CONSULT_STATUS_ORDER.reduce((sum, s) => sum + (counts[s] ?? 0), 0) : undefined;

  return (
    <div>
      <header className="page-header">
        <div>
          <h1 className="page-header__title">ນັດປຶກສາ</h1>
          <p className="page-header__subtitle">ໄກ່ເກ່ຍ, ມໍລະດົກ, ຢ່າຮ້າງ — ເນື້ອໃນເປັນຄວາມລັບ ເປີດອ່ານທີລະເລື່ອງ</p>
        </div>
      </header>

      <div className="group-chips" role="tablist" aria-label="ຕົວກອງສະຖານະ">
        {CONSULT_STATUS_ORDER.map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={status === s}
            className={status === s ? "group-chip is-active" : "group-chip"}
            onClick={() => setStatus(s)}
          >
            {CONSULT_STATUS_LABEL[s]} <span className="num">{counts ? num(counts[s] ?? 0) : "—"}</span>
          </button>
        ))}
        <button
          type="button"
          role="tab"
          aria-selected={status === null}
          className={status === null ? "group-chip is-active" : "group-chip"}
          onClick={() => setStatus(null)}
        >
          ທັງໝົດ <span className="num">{total === undefined ? "—" : num(total)}</span>
        </button>
      </div>

      <p className="lock-note" style={{ marginBottom: "var(--space-3)" }}>
        <Lock size={14} /> ລາຍການບໍ່ສະແດງເນື້ອໃນ — ການເປີດອ່ານແຕ່ລະເລື່ອງຖືກບັນທຶກໄວ້ໃນບັນທຶກການເຄື່ອນໄຫວ
      </p>

      <section className="table-scroll">
        {list.isPending && <div className="data-table__empty body-sm">ກຳລັງໂຫຼດ…</div>}
        {list.isError && (
          <div className="data-table__empty body-sm" style={{ color: "var(--red-600)" }}>
            ໂຫຼດບໍ່ສຳເລັດ: {list.error.message}{" "}
            <button type="button" className="btn-outline" onClick={() => void list.refetch()}>
              ລອງໃໝ່
            </button>
          </div>
        )}
        {list.data && (
          <table className="data-table">
            <thead>
              <tr>
                <th>ຫົວຂໍ້</th>
                <th>ສະຖານະ</th>
                <th>ຊ່ອງທາງ</th>
                <th>ບ້ານ</th>
                <th>ຂໍເມື່ອ</th>
                <th>ເວລານັດ</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="data-table__empty">
                    ບໍ່ມີການນັດປຶກສາໃນສະຖານະນີ້
                  </td>
                </tr>
              )}
              {list.data.items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{CONSULT_TOPIC_LABEL[c.topic]}</div>
                    <div className="caption num">#{shortId(c.id)}</div>
                  </td>
                  <td>
                    <span className={consultStatusBadge(c.status)}>{CONSULT_STATUS_LABEL[c.status]}</span>
                  </td>
                  <td className="body-sm">{CONTACT_MODE_LABEL[c.contact_mode]}</td>
                  <td className="body-sm">{placeLabel(c)}</td>
                  <td className="caption">{formatAgoLo(c.created_at)}</td>
                  <td className="body-sm num">{c.scheduled_at ? dateTimeLo(c.scheduled_at) : "—"}</td>
                  <td className="is-right">
                    <Link to={`/consultations/${c.id}`} className="btn-outline">
                      ເປີດອ່ານ
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {list.data?.has_more && (
        <p className="caption" style={{ marginTop: "var(--space-2)" }}>
          ສະແດງ {PAGE_LIMIT} ລາຍການລ້າສຸດ — ອາດຍັງບໍ່ຄົບ
        </p>
      )}
    </div>
  );
}
